import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import ProgressCircle from "../../ProgressCircle/ProgressCircle";
import { useNavigate } from "react-router-dom";
import PreLoader from "../../preloader/PreLoader";
import axiosInstance from "../../../api/axiosInstance";
import { openVerifyOtp } from "../../../store/slices/modalSlice";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput from "../../LoginModel/PhoneInput";
import {
  AccountFormData,
  FormErrors,
  UserProfile,
} from "../../../types/profile";
import { setUser } from "../../../store/slices/userSlice";
import { RootState } from "../../../store/store";
import { sendOtpFun } from "../../../types/sendOtpFun";

const AccountProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [message, setMessage] = useState("");
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const phoneInputRef = useRef<HTMLInputElement>(null);
  const alterPhoneInputRef = useRef<HTMLInputElement>(null);

  const initialFormData: AccountFormData = {
    UserFullName: "",
    UserEmail: "",
    UserNumber: "",
    password: "",
    address: "",
    passport: "",
    emiratesId: "",
    IsVerified: false,
    IsEmailVerified: false,
    gender: "",
    UserCountryCode: "+971",
    userAlternateCountryCode: "+971",
    alternatePhone: "",
    dateOfBirth: "",
    userNationality: "",
  };

  const [formData, setFormData] = useState<AccountFormData>(initialFormData);

  const handleChange = (
    field: keyof AccountFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    handleChange(name as keyof AccountFormData, value);
  };

  const isValidDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const formatDate = (isoDate: string) => isoDate.split("T")[0];

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{9}$/;

    if (!formData.UserFullName) errors.fullName = "Full Name is required";
    if (formData.UserEmail && !emailRegex.test(formData.UserEmail))
      errors.email = "Email is invalid";
    if (!formData.UserNumber) errors.phoneNumber = "Phone Number is required";
    else if (!phoneRegex.test(formData.UserNumber))
      errors.phoneNumber = "Phone Number is invalid";
    if (formData.alternatePhone && !phoneRegex.test(formData.alternatePhone))
      errors.alternatePhone = "Alternate Phone is invalid";
    if (formData.dateOfBirth && isNaN(Date.parse(formData.dateOfBirth)))
      errors.dateOfBirth = "Date of Birth is invalid";
    if (formData.dateOfBirth && !isValidDate(formData.dateOfBirth))
      errors.dateOfBirth = "Date of Birth is invalid";
    if (formData.userNationality && formData.userNationality.length < 2)
      errors.userNationality = "Nationality seems too short";
    if (!["Male", "Female", "Other"].includes(formData.gender))
      errors.gender = "Invalid gender selected";

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const otpVerified = useSelector((state: RootState) => state.modal.verifyOtp);

  useEffect(() => {
    if (otpVerified) {
      localStorage.removeItem("typeOfOtp");
      fetchUserProfileData();
    }
  }, [otpVerified]);

  const fetchUserProfileData = async () => {
    const login_info = localStorage.getItem("Login_info");
    if (!login_info) return navigate("/");

    try {
      const [response] = await Promise.all([
        axiosInstance.post(`/api/user/userProfile`, []),
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
      const userProfileData = response.data.userProfileData;
      if (!userProfileData) return;

      setUserProfile(userProfileData);
      dispatch(
        setUser({
          ...user,
          userId: userProfileData.RecordId,
          name: userProfileData.UserFullName,
          avatar: userProfileData.userProfilePicBase64,
          email: userProfileData.UserEmail,
        })
      );
      setFormData({
        ...initialFormData,
        ...userProfileData,
        dateOfBirth: userProfileData.dateOfBirth
          ? formatDate(userProfileData.dateOfBirth)
          : "",
      });
    } catch (error) {
      console.error("Error fetching user profile data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfileData();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateForm()) return;
    try {
      setIsSubmitting(true);
      const res = await axiosInstance.post(
        "/api/user/userProfileUpdate",
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`, // send token if needed
          },
        }
      );
      setMessage(res.data.message); // ✅ Show "Profile updated successfully"
      setUserProfile(res.data.userProfileData); // if you want to show updated profile
      dispatch(
        setUser({
          ...user,
          userId: res.data.userProfileData.RecordId,
          name: res.data.userProfileData.UserFullName,
          avatar: res.data.userProfileData.userProfilePicBase64,
          email: res.data.userProfileData.UserEmail,
        })
      );
    } catch (err) {
      setMessage("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEmailVeriftyModel = async () => {
    const apiUrl = `/api/openRoute/sendEmailOtp`;
    const postData = { userEmail: formData.UserEmail, type: "1" };
    try {
      const response = await sendOtpFun<typeof postData>(apiUrl, postData);
      if (response.status === 200) {
        localStorage.setItem("userEmail", formData.UserEmail);
        localStorage.setItem("typeOfOtp", "1");
        dispatch(openVerifyOtp());
      }
    } catch (error) {
      console.error("OTP Error:", error);
    }
  };
  const openVeriftyModel = async () => {
    const apiUrl = `/api/openRoute/sendOtp`;
    const postData = {
      userNumber: formData.UserNumber,
      UserCountryCode: formData.UserCountryCode,
      type: "0",
    };
    try {
      const response = await sendOtpFun<typeof postData>(apiUrl, postData);
      if (response.status === 200) {
        localStorage.setItem(
          "phoneNumber",
          JSON.stringify([
            {
              countryCode: formData.UserCountryCode,
              phoneNumber: formData.UserNumber,
            },
          ])
        );
        dispatch(openVerifyOtp());
      }
    } catch (error) {
      console.error("OTP Error:", error);
    }
  };

  if (isLoading) return <PreLoader />;

  return (
    <div className="row my-detail up-form layout-border rounded-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-1 p-3 border-bottom py-2 d-none d-md-flex">
        <div>
          <h1 className="ff-semibold text-black mb-1 font-18">My Detail</h1>
          <p className="font-14 text-muted mb-0">
            Easily add, update, or delete any information in your account
          </p>
        </div>
        <div>
          <ProgressCircle percent={userProfile?.profileCompletion || 70} />
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <h6 className="ff-semibold text-black mt-3 mb-md-2 mb-4 frm-hd">
          Personal information
        </h6>
        <div className="row">
          {message && <div className="success-text">{message}</div>}{" "}
          {/* ✅ Show success or error message */}
          <div className="col-md-6 mb-3">
            <div className="form-group mt-2">
              <Form.Control
                type="text"
                name="UserFullName"
                placeholder="Full Name"
                value={formData.UserFullName}
                onChange={handleInputChange}
              />
              <Form.Label
                htmlFor="fullName"
                className="ff-semibold text-black-50"
              >
                Full Name
              </Form.Label>
              {errors && errors?.fullName && (
                <div className="error-text">{errors.fullName}</div>
              )}
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="form-group mt-2">
              <Form.Control
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                placeholder="Date of Birth"
                value={formData.dateOfBirth}
                max={new Date().toISOString().split("T")[0]}
                onChange={handleInputChange}
              />
              <Form.Label
                htmlFor="dateOfBirth"
                className="ff-semibold text-black-50"
              >
                Date of Birth
              </Form.Label>
              {errors && errors?.dateOfBirth && (
                <div className="error-text">{errors.dateOfBirth}</div>
              )}
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="form-group mt-2">
              <Form.Label
                className="ff-semibold text-black-50 mb-1"
                style={{ top: "-10px", left: "6px", fontSize: "0.8em" }}
              >
                Gender
              </Form.Label>
              <div
                className="gender-select-wrap d-flex align-items-center gap-4 px-3 py-2 border rounded"
                style={{ height: "45px" }}
              >
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="radio"
                    id="genderMale"
                    name="gender"
                    className="me-2 up-radio"
                    value="Male"
                    checked={formData.gender === "Male"}
                    onChange={handleInputChange}
                  />
                  <p className="mb-0 font-13 ff-semibold">Male</p>
                </div>
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="radio"
                    id="genderFemale"
                    name="gender"
                    className="me-2 up-radio"
                    value="Female"
                    checked={formData.gender === "Female"}
                    onChange={handleInputChange}
                  />
                  <p className="mb-0 font-13 ff-semibold">Female</p>
                </div>
              </div>
              {errors && errors?.gender && (
                <div className="error-text">{errors.gender}</div>
              )}
            </div>
          </div>
        </div>

        <div className="border-bottom" />

        <h6 className="ff-semibold text-black frm-hd mt-3 mb-md-2 mb-4">
          Contact information
        </h6>
        <div className="row">
          <div className="col-md-6 mb-3">
            <div className="form-group mt-2 position-relative unique-verifiable">
              <PhoneInput
                placeholder="Phone Number"
                phoneNumber={formData.UserNumber}
                countryCode={formData.UserCountryCode}
                phoneInputRef={phoneInputRef}
                onPhoneNumberChange={(value) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    UserNumber: value,
                  }))
                }
                onCountryCodeChange={(value) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    UserCountryCode: value,
                  }))
                }
              />
              <div className="verification-box d-flex align-items-center">
                {formData.IsVerified ? (
                  <span className="text-success font-12 ff-semibold me-2">
                    <i className="bi bi-check-circle-fill me-1" /> Verified
                  </span>
                ) : (
                  <span
                    className="text-primary font-12 ff-semibold verify-btn"
                    onClick={() => openVeriftyModel()}
                  >
                    Verify
                  </span>
                )}
              </div>
            </div>
            {errors && errors?.phoneNumber && (
              <div className="error-text">{errors.phoneNumber}</div>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <div className="form-group mt-2 position-relative unique-verifiable">
              <PhoneInput
                placeholder="Alternate Phone No"
                phoneNumber={formData.alternatePhone}
                countryCode={formData.userAlternateCountryCode}
                phoneInputRef={alterPhoneInputRef}
                require={false}
                onPhoneNumberChange={(value) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    alternatePhone: value,
                  }))
                }
                onCountryCodeChange={(value) =>
                  setFormData((prevData) => ({
                    ...prevData,
                    userAlternateCountryCode: value,
                  }))
                }
              />
            </div>
            {errors && errors?.alternatePhone && (
              <div className="error-text">{errors.alternatePhone}</div>
            )}
          </div>
          <div className="col-md-6 mb-3">
            <div className="form-group mt-2 position-relative unique-verifiable">
              <Form.Control
                type="email"
                id="email"
                name="UserEmail"
                value={formData.UserEmail}
                onChange={handleInputChange}
                placeholder="Email Address"
              />
              <Form.Label htmlFor="email" className="ff-semibold text-black-50">
                Email Address
              </Form.Label>
              <div className="verification-box d-flex align-items-center">
                {formData.IsEmailVerified ? (
                  <span className="text-success font-12 ff-semibold me-2">
                    <i className="bi bi-check-circle-fill me-1" /> Verified
                  </span>
                ) : (
                  <span
                    className="text-primary font-12 ff-semibold verify-btn"
                    onClick={() => openEmailVeriftyModel()}
                  >
                    Verify
                  </span>
                )}
              </div>
            </div>
            {errors && errors?.email && (
              <div className="error-text">{errors.email}</div>
            )}
          </div>
        </div>

        <div className="text-center d-flex justify-content-center col-12 mt-3 mb-md-5 mb-4">
          <button
            className="btn btn-primary text-white bg-main w-25 font-14 ff-semibold bt--cart"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Profile"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AccountProfile;
